import { useCallback, useEffect, useRef } from 'react';
import { useMount } from 'react-use';

import IconNewNavRight from '../../../assets/svg/IconNewNavRight';
import IconShopMore from '../../../assets/svg/IconShopMore';
import { useOrientation } from '../../../boot/OrientationContext';
import { white } from '../../../constants/color';
import { checkItemViewed } from '../../../context/devLogFirehose';
import { translate } from '../../../i18n';
import { ListLoadingTypes } from '../../../types/Common';
import { OnSaleItem as OnSaleProduct } from '../../../types/OnSaleResponse';
import ItemSources from '../../../utils/ItemSources';
import * as navigationService from '../../../utils/navigationService';
import routeUtils from '../../../utils/routes';
import FlatListWithImpression, {
  FlatListWithImpressionType,
} from '../../FlatListWithImpression';
import { itemProducer } from '../../FlatListWithImpression/utils';
import { useReduxHooks } from './hooks';
import LoadingState from './LoadingState';
import OnSaleItem from './OnSaleItem';
import S, { linearGradientProps } from './styles';

type RenderItem = (args: { item: string; index: number }) => JSX.Element;

const keyExtractor = item => item;
const sources = new ItemSources<OnSaleProduct>({
  productKey: product => `${product.id}`,
  productSource: product => product.source,
});

export const layoutName = 'index_on_sale';

export default () => {
  const isLandscape = useOrientation();
  const productsAmount = isLandscape ? 20 : 10;
  const {
    isFetching,
    loadingType,
    products,
    isHomePageReload,
    getIndexOnSale,
  } = useReduxHooks(productsAmount);
  const flatList = useRef<FlatListWithImpressionType>(null);

  const getList = useCallback(
    (type: ListLoadingTypes) => {
      getIndexOnSale({ success: sources.processSource, loadingType: type });
    },
    [getIndexOnSale]
  );

  useMount(() => {
    getList('new');
  });

  useEffect(() => {
    const onRefresh = () => {
      flatList.current?.clearImpressionAccumulatedItems();
      getList('reload');
    };

    if (isHomePageReload) {
      onRefresh();
    }
  }, [getList, isHomePageReload]);

  const renderItem = useCallback<RenderItem>(({ item, index }) => {
    return (
      <OnSaleItem
        id={item}
        index={index}
        layoutName={layoutName}
        shouldDisplayImpressionForDebug={checkItemViewed(
          flatList.current?.accumulatedItems,
          item
        )}
        source={sources.get(item)}
      />
    );
  }, []);

  if (isFetching && loadingType === 'new') {
    return <LoadingState />;
  }

  return (
    <S.Structure>
      <S.Banner pointerEvents="none">
        <S.BannerBackground
          source={require('./assets/img_discount_title.png')}
        />
        <S.BannerText maxFontSizeMultiplier={1}>
          {translate('index-on-sale-title-center')}
        </S.BannerText>
      </S.Banner>
      <S.Container {...linearGradientProps}>
        <S.TitleContainer>
          <S.Title>{translate('index-on-sale-title-left')}</S.Title>
          <S.Blank />
          <S.TitleRight
            onPress={() => {
              navigationService.navigate('OnSalePage');
            }}
            accessibilityRole="link"
            href={routeUtils.compileToPath('OnSalePage')}
          >
            <S.Title>{translate('index-on-sale-title-right')}</S.Title>
            <S.IconContainer>
              <IconNewNavRight fillColor={white} size={12} />
            </S.IconContainer>
          </S.TitleRight>
        </S.TitleContainer>
        <FlatListWithImpression
          ref={flatList}
          data={products}
          renderItem={renderItem}
          dataProducer={itemProducer}
          keyExtractor={keyExtractor}
          ListHeaderComponent={S.ListHeader}
          ListFooterComponent={() => {
            return (
              <S.ListFooter
                onPress={() => {
                  navigationService.navigate('OnSalePage');
                }}
                accessibilityRole="link"
                href={routeUtils.compileToPath('OnSalePage')}
              >
                <S.ListFooterIcon>
                  <IconShopMore fillColor={white} />
                </S.ListFooterIcon>
                <S.ListFooterText>{translate('shop-more')}</S.ListFooterText>
              </S.ListFooter>
            );
          }}
          sources={sources}
          showsHorizontalScrollIndicator={false}
          horizontal
          layoutName={layoutName}
        />
      </S.Container>
    </S.Structure>
  );
};
